import React, { useEffect, useRef, useState } from 'react';
import { Facebook, ArrowRight } from 'lucide-react';

const FACEBOOK_URL = 'https://www.facebook.com/LivingHopeKnox/';
const PAGE_HREF = 'https://www.facebook.com/LivingHopeKnox';
const HEIGHT = 680;

// Responsive Facebook Page Plugin embed.
// The FB plugin renders at a fixed pixel width, so we measure the available
// container width and request that exact width (clamped 180-500) from Facebook,
// re-rendering when the viewport size changes. This keeps the live feed fully
// on-screen on both mobile and desktop.
const FacebookFeed = () => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(340);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let frame;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const available = Math.floor(el.clientWidth);
        const clamped = Math.max(180, Math.min(500, available));
        setWidth((prev) => (Math.abs(prev - clamped) > 4 ? clamped : prev));
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, []);

  const src =
    `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(PAGE_HREF)}` +
    `&tabs=timeline&width=${width}&height=${HEIGHT}&small_header=false` +
    `&adapt_container_width=true&hide_cover=false&show_facepile=true`;

  return (
    <div ref={wrapRef} className="w-full flex justify-center lg:justify-end">
      <div
        className="relative bg-[#0b2027] rounded-xl shadow-2xl overflow-hidden border border-white/10"
        style={{ width: `${width}px`, minHeight: `${HEIGHT}px` }}
      >
        {/* Branded fallback shown until / unless the Facebook feed renders on top */}
        <div className="absolute inset-0 z-0 flex flex-col items-center justify-center text-center p-8">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#1877F2] mb-6">
            <Facebook className="h-8 w-8 text-white" />
          </div>
          <h3 className="font-serif-display text-white text-2xl mb-3">Our Facebook Feed</h3>
          <p className="text-white/60 text-sm max-w-xs mb-6">
            Our latest posts and live services appear here. We go live every Sunday at 10:30 AM.
          </p>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#c9a24b] uppercase tracking-[0.18em] text-xs font-600"
          >
            Open on Facebook <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        <iframe
          key={width}
          title="Living Hope Facebook Feed"
          src={src}
          width={width}
          height={HEIGHT}
          className="relative z-10 block"
          style={{ border: 'none', overflow: 'hidden', width: `${width}px`, height: `${HEIGHT}px` }}
          scrolling="no"
          frameBorder="0"
          allowFullScreen={true}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        />
      </div>
    </div>
  );
};

export default FacebookFeed;
